import React, { useMemo } from 'react';

import { useAutoNavigate } from '@betterment/js-runtime';
import { Box, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { CenteredWorkflowLayout, HR, WorkflowHeader } from '@betterment/style-closet-react';

import FourOhOneKAccountRow from './components/FourOhOneKAccountRow';
import FourOhOneKByEmployer from './components/FourOhOneKByEmployer';
import { DeferralSetting, DeferralSettings } from './data';
import { useDeferralSettingsQuery } from './hooks';

import { WithLayout } from '../RetailApp/components/WithLayout';
import { TextShimmerLoader } from '../Shared/components/ShimmerLoaders';
import { urlBuilder } from '../Shared/urlBuilder';

const ShimmerLoader = () => {
  return (
    <Box>
      <HR />
      <Box
        css={{
          mb: 'space.300',
          mt: 'space.300',
        }}
      >
        <TextShimmerLoader numLines={3} />
      </Box>
    </Box>
  );
};

const FourOhOneKAccountSelect: React.FC<React.PropsWithChildren> = () => {
  const { isLoading, deferralSettings } = useDeferralSettingsQuery();
  const autoNavigate = useAutoNavigate();

  const urlSearchParams = new URLSearchParams(window.location.search);
  const returnToPath =
    urlSearchParams.get('return_to') || urlSearchParams.get('returnTo') || urlBuilder.appHome.absolute();

  const deferralSettingsByEmployer = useMemo(() => {
    if (deferralSettings) {
      const byEmployer: { [key: string]: DeferralSettings } = {};
      deferralSettings.forEach((deferralSetting: DeferralSetting) => {
        byEmployer[deferralSetting.employer.id] = byEmployer[deferralSetting.employer.id] || [];
        byEmployer[deferralSetting.employer.id].push(deferralSetting);
      });
      return byEmployer;
    }
    return null;
  }, [deferralSettings]);

  const hasMultipleEmployers = useMemo(
    () => (deferralSettingsByEmployer ? Object.keys(deferralSettingsByEmployer).length > 1 : false),
    [deferralSettingsByEmployer]
  );

  const shouldShowHelpText = useMemo(() => {
    if (deferralSettings) {
      return !deferralSettings.some(
        (deferralSetting: DeferralSetting) =>
          deferralSetting.account.anticipatedBalance.cents > 0 ||
          (deferralSetting.value.__typename === 'PayrollContributionConfigValueAmount' &&
            deferralSetting.value.amount.cents > 0) ||
          (deferralSetting.value.__typename === 'PayrollContributionConfigValuePercent' &&
            deferralSetting.value.percent > 0)
      );
    }

    return true;
  }, [deferralSettings]);

  return (
    <WithLayout parts={{ sidebar: false }}>
      <CenteredWorkflowLayout variant="five-column">
        <CenteredWorkflowLayout.Slot name="header">
          <WorkflowHeader title="401(k) deposit settings" closeHandler={() => void autoNavigate(returnToPath)} />
        </CenteredWorkflowLayout.Slot>
        <CenteredWorkflowLayout.Slot name="content">
          <Box css={{ marginTop: '.8rem' }}>
            <Text variant="text400" css={{ fontWeight: 600 }}>
              Which 401(k) contribution rate do you want to set?
            </Text>
          </Box>
          {shouldShowHelpText && (
            <Box style={{ marginBottom: '3.2rem', marginTop: '2.6rem' }}>
              <Text>
                Both a traditional and Roth 401(k)s give you tax benefits—the key difference comes down to when you get
                them. Not sure which to pick?{' '}
                <SambaAutoLink target="_blank" to={urlBuilder.getTradAndRoth401kEmployeeResourceUrl()}>
                  Learn more about traditional vs. Roth 401(k)
                </SambaAutoLink>
              </Text>
            </Box>
          )}
          {isLoading && (
            <>
              <ShimmerLoader />
              <ShimmerLoader />
            </>
          )}
          {!isLoading && !hasMultipleEmployers && (
            <Box css={{ marginBottom: '1.6rem', marginTop: '3.2rem' }}>
              {deferralSettings?.map(deferralSetting => (
                <FourOhOneKAccountRow
                  deferralSetting={deferralSetting}
                  shouldShowHelpText={shouldShowHelpText}
                  key={deferralSetting.id}
                />
              ))}
            </Box>
          )}
          {!isLoading &&
            deferralSettingsByEmployer &&
            hasMultipleEmployers &&
            Object.keys(deferralSettingsByEmployer).map(employerId => (
              <FourOhOneKByEmployer
                deferralSettings={deferralSettingsByEmployer[employerId]}
                shouldShowHelpText={shouldShowHelpText}
                key={employerId}
              />
            ))}
          <HR />
        </CenteredWorkflowLayout.Slot>
      </CenteredWorkflowLayout>
    </WithLayout>
  );
};
export default FourOhOneKAccountSelect;
